import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase-config';
import { AI_PIPELINE_SCHEMA_V2 } from '../../../constants/aiPipelineModes';
import { seedSchoolSchemaFromRegistration } from './seedSchoolSchemaFromRegistration';
import { startEvidenceAnalysisJob } from './evidenceAnalysisJob';
import { runSchemaV2EvidenceWorkersUntilDone } from './kickEvidenceWorkers';
import { buildSoftEvidenceAndClaimsMap } from './softEvidenceAndClaims';

/**
 * Map evidenceAnalysisJobs/{sid} → coarse Evidence Track lights for registry UI.
 * Analysis is independent of reports/{sid}; this never requires a report doc.
 */
export function evidenceJobToRefineryEvidence(job) {
  if (!job) {
    return { step: 0, label: '' };
  }
  const queued = Number(job.queued || 0);
  const analysing = Number(job.analysing || 0);
  const total = Number(job.total || 0);
  const failed = Number(job.failed || 0);
  const status = String(job.status || '');

  const complete =
    status === 'complete' ||
    (total > 0 && queued === 0 && analysing === 0) ||
    (total === 0 && (status === 'complete' || status === 'ready'));

  if (complete) {
    let label = 'v2_ready';
    if (failed > 0) label = 'v2_ready_with_failures';
    else if (total === 0) label = 'v2_ready_schema_only';
    else if (Number(job.skipped || 0) > 0 && Number(job.skipped || 0) === total) {
      label = 'v2_ready_photos_skipped';
    }
    return { step: 4, label, pipeline: AI_PIPELINE_SCHEMA_V2 };
  }

  if (queued > 0 || analysing > 0 || status === 'running' || status === 'queued') {
    return { step: 2, label: 'v2_job_running', pipeline: AI_PIPELINE_SCHEMA_V2 };
  }

  if (status || total > 0) {
    return { step: 2, label: 'v2_job_queued', pipeline: AI_PIPELINE_SCHEMA_V2 };
  }

  return { step: 0, label: '' };
}

export function isSchemaV2EvidenceReady(job) {
  return evidenceJobToRefineryEvidence(job).step >= 4;
}

/** Best-effort mirror onto reports/{sid} only if that doc already exists (report writing path). */
async function mirrorRefineryToReportIfPresent(schoolId, evidence, job) {
  const reportRef = doc(db, 'reports', schoolId);
  const snap = await getDoc(reportRef);
  if (!snap.exists()) return;
  await updateDoc(reportRef, {
    'meta.refinery.evidence': {
      step: evidence.step,
      label: evidence.label,
      pipeline: AI_PIPELINE_SCHEMA_V2,
    },
    'meta.aiPipeline': AI_PIPELINE_SCHEMA_V2,
    'meta.v2EvidenceJob': {
      status: job.status,
      total: job.total,
      queued: job.queued,
      skipped: job.skipped,
      done: job.done,
      failed: job.failed || 0,
      updatedAt: serverTimestamp(),
    },
  }).catch(() => {});
}

/**
 * Schema v2 evidence analysis entry (additive).
 * Seeds schema, opens per-file job board, kicks server workers.
 * Does NOT require reports/{sid} — analysis is independent of report writing.
 */
export async function runRefineryExtractionSchemaV2(schoolId, deps = {}) {
  const { actor } = deps;

  await seedSchoolSchemaFromRegistration(schoolId);

  const job = await startEvidenceAnalysisJob(schoolId, actor || {});

  if (Number(job.queued || 0) > 0) {
    runSchemaV2EvidenceWorkersUntilDone(schoolId).catch((err) => {
      console.error('schema_v2 worker chain failed', schoolId, err);
    });
  } else {
    buildSoftEvidenceAndClaimsMap(schoolId).catch((err) => {
      console.error('schema_v2 claims map failed', schoolId, err);
    });
  }

  const evidence = evidenceJobToRefineryEvidence(job);
  await mirrorRefineryToReportIfPresent(schoolId, evidence, job);

  return {
    job,
    pipeline: AI_PIPELINE_SCHEMA_V2,
    evidenceStep: evidence.step,
    evidenceLabel: evidence.label,
  };
}
